import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Tracker hooks for the per-country immigration view.
// Three thin CRUD wrappers — documents, milestones, costs — that
// share the same shape (list, loading, create, update, remove, refetch).

function makeCrudHook(table, defaultCountrySlugAware = true) {
  return function useCrud(countrySlug) {
    const { user } = useAuth()
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const initialLoadedRef = useRef(false)

    const fetch = useCallback(async () => {
      if (!supabaseConfigured || !user) {
        setLoading(false)
        return
      }
      if (!initialLoadedRef.current) setLoading(true)
      let query = supabase
        .from(table)
        .select('*')
        .eq('user_id', user.id)
      if (defaultCountrySlugAware && countrySlug) {
        // Include both country-specific rows AND rows that apply to
        // any country (country_slug IS NULL) — useful for general
        // documents like passport.
        query = query.or(`country_slug.eq.${countrySlug},country_slug.is.null`)
      }
      const { data } = await query
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })
      setItems(data || [])
      initialLoadedRef.current = true
      setLoading(false)
    }, [user, countrySlug])

    useEffect(() => { fetch() }, [fetch])

    const create = useCallback(async (values) => {
      if (!user) return null
      const payload = { ...values, user_id: user.id }
      const { data, error } = await supabase.from(table).insert(payload).select().single()
      if (error) return null
      await fetch()
      return data
    }, [user, fetch])

    const update = useCallback(async (id, patch) => {
      if (!user) return false
      const { error } = await supabase
        .from(table)
        .update(patch)
        .eq('id', id)
        .eq('user_id', user.id)
      if (error) return false
      await fetch()
      return true
    }, [user, fetch])

    const remove = useCallback(async (id) => {
      if (!user) return false
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
      if (error) return false
      await fetch()
      return true
    }, [user, fetch])

    return { items, loading, create, update, remove, refetch: fetch }
  }
}

export const useImmigrationDocuments  = makeCrudHook('immigration_documents', true)
export const useImmigrationMilestones = makeCrudHook('immigration_milestones', true)
export const useImmigrationCosts      = makeCrudHook('immigration_costs', true)
